/**
 * A page's title, subtitle and back arrow. Inside the shell they render in the
 * top bar (the provider holds the bar's slot); the page's own actions stay in
 * the page, in a row at the top of its content. A detail page passes `back`:
 * the bar then starts with an icon-only arrow to the parent list, named "Back
 * to <name>". Without a provider (tests, stand-alone use) everything renders
 * in place.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type MouseEventHandler,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const APP_TITLE = 'Wynxx Discover';
const TITLE_ID = 'page-title';

/** The id of the `h1` that names the `main` landmark. */
export const PAGE_TITLE_ID = TITLE_ID;

interface Owner {
  id: string;
  title: string;
}

interface SlotContext {
  slot: HTMLElement | null;
  setSlot: (element: HTMLElement | null) => void;
  owner: Owner | null;
  /** Last mount wins: the newest head takes the slot. */
  claim: (id: string) => void;
  retitle: (owner: Owner) => void;
  release: (id: string) => void;
}

const Slot = createContext<SlotContext | null>(null);

/** The callback ref for the element the top bar shows the page head in. */
export function usePageHeadSlot(): (element: HTMLElement | null) => void {
  return useContext(Slot)?.setSlot ?? noopRef;
}
const noopRef = () => undefined;

/** `document.title` follows the owner; restored only while it is still the title this owner set. */
function useDocumentTitle(title: string | undefined) {
  useEffect(() => {
    if (!title) return;
    const set = `${title} · ${APP_TITLE}`;
    document.title = set;
    return () => {
      if (document.title === set) document.title = APP_TITLE;
    };
  }, [title]);
}

/**
 * True when the heading focus was moved to has left the DOM and taken focus
 * with it (a loading head replaced by the loaded one). Focus that moved on from
 * a heading still in place is the user's: stop following it.
 */
function handedOver(focused: { current: HTMLElement | null }): boolean {
  const heading = focused.current;
  if (!heading) return false;
  if (heading.isConnected) {
    if (document.activeElement !== heading) focused.current = null;
    return false;
  }
  return document.activeElement === document.body;
}

/**
 * After a route change the new page's `h1` takes focus, once its head has
 * registered (it may render after data loads). Redirects before any page has
 * shown (`/` to `/dashboard`) are still the initial load and keep the
 * browser's focus. A focused heading that a loading head hands over to the
 * loaded one passes focus on rather than dropping it to the body.
 */
function useRouteFocus(ownerId: string | undefined) {
  const { pathname } = useLocation();
  const previous = useRef(pathname);
  const shown = useRef(false);
  const pending = useRef(false);
  const focused = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (previous.current === pathname) return;
    previous.current = pathname;
    pending.current = shown.current;
  }, [pathname]);
  useEffect(() => {
    if (!ownerId) return;
    shown.current = true;
    if (!pending.current && !handedOver(focused)) return;
    // The h1 lives in the bar (or in place without a provider): look it up in the document.
    const heading = document.getElementById(TITLE_ID);
    if (!heading) return;
    pending.current = false;
    focused.current = heading;
    heading.focus();
  });
}

export function PageHeadSlotProvider({ children }: { children: ReactNode }) {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const [owner, setOwner] = useState<Owner | null>(null);
  const claim = useCallback((id: string) => setOwner({ id, title: '' }), []);
  const retitle = useCallback((next: Owner) => setOwner((current) => (current?.id === next.id ? next : current)), []);
  const release = useCallback((id: string) => setOwner((current) => (current?.id === id ? null : current)), []);
  useDocumentTitle(owner?.title);
  useRouteFocus(owner?.id);
  const value = useMemo(() => ({ slot, setSlot, owner, claim, retitle, release }), [slot, owner, claim, retitle, release]);
  return <Slot.Provider value={value}>{children}</Slot.Provider>;
}

/** The full text of a subtitle that is more than a string, for the tooltip a truncated line needs. */
function fullText(element: HTMLElement | null) {
  if (element?.textContent) element.title = element.textContent;
}

function HeadText({ title, subtitle, back }: PageHeadProps) {
  const text = (
    <div className="header-text">
      <h1 className="page-title" id={TITLE_ID} tabIndex={-1} title={title}>
        {title}
      </h1>
      {subtitle ? (
        <div className="page-sub" title={typeof subtitle === 'string' ? subtitle : undefined} ref={typeof subtitle === 'string' ? undefined : fullText}>
          {subtitle}
        </div>
      ) : null}
    </div>
  );
  if (!back) return text;
  const label = `Back to ${back.name}`;
  return (
    <div className="header-back">
      <Link to={back.to} className="btn btn-ghost btn-icon header-back-arrow" aria-label={label} title={label} onClick={back.onClick}>
        <ArrowLeft aria-hidden="true" />
      </Link>
      {text}
    </div>
  );
}

export interface PageHeadProps {
  title: string;
  subtitle?: ReactNode;
  /** `onClick` lets a page that holds unsaved work ask before the arrow leaves (it may `preventDefault`). */
  back?: { to: string; name: string; onClick?: MouseEventHandler<HTMLAnchorElement> };
  /** The page's actions: they stay in the page. */
  children?: ReactNode;
}

export function PageHead(props: PageHeadProps) {
  const { title, children } = props;
  const context = useContext(Slot);
  const id = useId();
  const { claim, retitle, release } = context ?? {};
  useEffect(() => {
    claim?.(id);
    return () => release?.(id);
  }, [id, claim, release]);
  // Separate from the claim: a title change must not take the slot back from a newer head.
  useEffect(() => retitle?.({ id, title }), [id, title, retitle]);

  if (!context) {
    return (
      <div className="page-head">
        <div className="header-page">
          <HeadText {...props} />
        </div>
        {children && <div className="page-actions">{children}</div>}
      </div>
    );
  }
  const owns = context.owner?.id === id && context.slot;
  return (
    <>
      {owns && createPortal(<HeadText {...props} />, context.slot!)}
      {children && <div className="page-actions page-actions-top">{children}</div>}
    </>
  );
}
